import type { CohortCell, CohortGrid, CohortRow } from '../../models/cohort.types'

import type { ReportRow } from '@/features/reports'

function finite(value: unknown): number | null {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function startDate(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

export function cohortMetrics(rows: readonly ReportRow[]): string[] {
  return [
    ...new Set(
      rows.flatMap((row) => (typeof row.metric_name === 'string' ? [row.metric_name] : [])),
    ),
  ]
}

export function buildCohortGrid(rows: readonly ReportRow[], metric: string): CohortGrid {
  const byDate = new Map<string, Map<number, { value: number; installs: number }>>()
  for (const row of rows) {
    if (row.metric_name !== metric) continue
    const date = startDate(row.start_date)
    const period = finite(row.cohort_period)
    const value = finite(row.metric_value)
    const installs = finite(row.mmp_app_installs)
    if (!date || period === null || value === null || installs === null) continue
    // v3 `AppAcquisitionCohort.vue:141` only kept truthy `metric_value`s: a zero past period
    // 0 is a not-yet-matured slot and renders blank, never as "0.00%".
    if (period > 0 && value === 0) continue
    const periods = byDate.get(date) ?? new Map()
    periods.set(period, { value, installs })
    byDate.set(date, periods)
  }

  const result: CohortRow[] = []
  for (const [date, periods] of byDate) {
    const denominator = periods.get(0)?.value
    if (denominator === undefined || denominator <= 0) continue
    const cells = [...periods.entries()]
      .sort(([left], [right]) => left - right)
      .map<CohortCell>(([period, datum]) => ({
        cohort_period: period,
        metric_value: datum.value,
        mmp_app_installs: datum.installs,
        percent: (datum.value / denominator) * 100,
      }))
    result.push({ start_date: date, cells })
  }
  result.sort((left, right) => Date.parse(left.start_date) - Date.parse(right.start_date))
  return {
    metric,
    rows: result,
    // Columns are positional by `cohort_period`, so a gap keeps its slot instead of shifting
    // later periods left.
    width: result.reduce(
      (max, row) => Math.max(max, ...row.cells.map((cell) => cell.cohort_period + 1)),
      0,
    ),
  }
}
